"use client"
import { useState, useEffect, useRef } from "react"
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  Tooltip,
  Avatar,
  useTheme,
  alpha,
  IconButton,
  useMediaQuery,
  Menu,
  MenuItem,
} from "@mui/material"
import { Link as RouterLink, useLocation } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { useWebSocket } from "../context/WebSocketContext"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import HomeIcon from "@mui/icons-material/Home"
import { FaDiscord } from "react-icons/fa"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import LogoutIcon from "@mui/icons-material/Logout"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip"
import GavelIcon from "@mui/icons-material/Gavel"
import ContactMailIcon from "@mui/icons-material/ContactMail"
import DnsIcon from "@mui/icons-material/Dns"
import ListAltIcon from "@mui/icons-material/ListAlt"
import PeopleAltIcon from "@mui/icons-material/PeopleAlt"
import DownloadIcon from "@mui/icons-material/Download"
import config from "../config"
import LogoutConfirmationModal from "./LogoutConfirmationModal"
import { useToast, TOAST_SEVERITY } from "../context/ToastContext"
import { LoginWithDiscordBtn } from "./LoginWithDiscordBtn"

const Navbar = () => {
  const theme = useTheme()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { wsStatus } = useWebSocket()
  const { showToast } = useToast()
  const [scrolled, setScrolled] = useState(false)
  const navRef = useRef(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"))
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))

  // State for logout confirmation modal
  const [logoutModalOpen, setLogoutModalOpen] = useState(false)

  // State for Other dropdown menu
  const [otherMenuAnchorEl, setOtherMenuAnchorEl] = useState(null)
  const otherMenuOpen = Boolean(otherMenuAnchorEl)

  // State for Download dropdown menu
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null)
  const downloadMenuOpen = Boolean(downloadMenuAnchorEl)

  // Helper to format Discord avatar URL
  function getDiscordAvatarUrl(user) {
    if (!user || !user.id) return ""
    if (!user.avatar) {
      // Default avatar (use Discord CDN default)
      const defaultId = Number.parseInt(user.id) % 5
      return `https://cdn.discordapp.com/embed/avatars/${defaultId}.png`
    }
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`
  }

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [scrolled])

  // Check if arrows should be shown
  useEffect(() => {
    const checkScroll = () => {
      if (navRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = navRef.current
        setShowLeftArrow(scrollLeft > 0)
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5) // 5px buffer
      }
    }

    // Initial check
    checkScroll()

    // Add event listener for resize
    window.addEventListener("resize", checkScroll)

    // Add event listener for scroll on the nav container
    if (navRef.current) {
      navRef.current.addEventListener("scroll", checkScroll)
    }

    return () => {
      window.removeEventListener("resize", checkScroll)
      if (navRef.current) {
        navRef.current.removeEventListener("scroll", checkScroll)
      }
    }
  }, [navRef.current, isMediumScreen])

  // Handle logout button click
  const handleLogoutClick = () => {
    setLogoutModalOpen(true)
  }

  // Confirm logout action
  const confirmLogout = () => {
    setLogoutModalOpen(false)
    logout()
    showToast("You have been logged out successfully", TOAST_SEVERITY.SUCCESS)
  }

  // Handle external navigation
  const handleExternalNavigation = (url) => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  // Scroll the nav tabs
  const scrollNav = (direction) => {
    if (navRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200
      navRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" })
    }
  }

  // Handle Other menu open
  const handleOtherMenuOpen = (event) => {
    setOtherMenuAnchorEl(event.currentTarget)
  }

  // Handle Other menu close
  const handleOtherMenuClose = () => {
    setOtherMenuAnchorEl(null)
  }

  // Handle Download menu open
  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchorEl(event.currentTarget)
  }

  // Handle Download menu close
  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchorEl(null)
  }

  // Trigger download of the latest installer via a GET to the release URL
  const handleDownloadLatestInstaller = () => {
    handleDownloadMenuClose()
    const link = document.createElement("a")
    link.href = config.LAUNCHER_DOWNLOAD_LINK
    link.download = ""
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Determine color, tooltip, and animation for WebSocket status
  let wsColor = theme.palette.warning.main
  let wsTooltip = "Connecting to server..."
  let wsAnimation = undefined
  let wsGlow = "0 0 8px rgba(255, 152, 0, 0.8)"

  if (wsStatus === "connected") {
    wsColor = theme.palette.success.main
    wsTooltip = "Connected to DarkEdge server"
    wsGlow = "0 0 12px rgba(76, 175, 80, 1), 0 0 24px rgba(76, 175, 80, 0.7)"
  } else if (wsStatus === "disconnected") {
    wsColor = theme.palette.error.main
    wsTooltip = "Disconnected from DarkEdge server"
    wsGlow = "0 0 12px rgba(244, 67, 54, 1), 0 0 24px rgba(244, 67, 54, 0.7)"
  } else if (wsStatus === "connecting") {
    wsAnimation = "pulse 1.2s infinite"
    wsGlow = "0 0 12px rgba(255, 152, 0, 0.9), 0 0 24px rgba(255, 152, 0, 0.6)"
  }

  // Check if a route is active
  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/"
    }
    return location.pathname.startsWith(path)
  }

  // Navigation links
  const navLinks = [
    { path: "/", label: "Home", icon: <HomeIcon />, external: false },
    { path: "/features", label: "Features", icon: <ListAltIcon />, external: false },
    {
      path: "https://discord.gg/4Sy9nGasJV",
      label: "Discord",
      icon: <FaDiscord />,
      external: true,
      name: "Discord Server",
    },
    { path: "/pricing", label: "Pricing", icon: <CreditCardIcon />, external: false },
    { path: "/vps", label: "VPS", icon: <DnsIcon />, external: false },
    ...(true ? [{ path: "/in-range-players", label: "Tracker", icon: <PeopleAltIcon />, external: false }] : []),
  ]

  // Download dropdown (rendered after Home)
  const downloadDropdown = (
    <Box key="download-dropdown" sx={{ display: "flex", flexShrink: 0 }}>
      <Button
        id="download-menu-button"
        aria-controls={downloadMenuOpen ? "download-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={downloadMenuOpen ? "true" : undefined}
        onClick={handleDownloadMenuOpen}
        startIcon={!isSmallScreen && <DownloadIcon />}
        endIcon={!isSmallScreen && <KeyboardArrowDownIcon />}
        sx={{
          color: "white",
          textTransform: "none",
          px: { xs: 1.5, sm: 2 },
          py: 1,
          borderRadius: 0,
          position: "relative",
          overflow: "visible",
          whiteSpace: "nowrap",
          minWidth: 0,
          flexShrink: 0,
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
          },
        }}
      >
        {isSmallScreen ? <DownloadIcon /> : <Typography variant="body1">Download</Typography>}
      </Button>
      <Menu
        id="download-menu"
        anchorEl={downloadMenuAnchorEl}
        open={downloadMenuOpen}
        onClose={handleDownloadMenuClose}
        MenuListProps={{
          "aria-labelledby": "download-menu-button",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            background: "rgba(13, 15, 25, 0.95)",
            border: "1px solid rgba(0, 229, 255, 0.2)",
            boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <MenuItem
          onClick={handleDownloadLatestInstaller}
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
            minWidth: 180,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <DownloadIcon fontSize="small" />
            <Typography variant="body2">Latest Installer</Typography>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )

  // Other menu links
  const otherLinks = [
    { path: "/privacy", label: "Privacy Policy", icon: <PrivacyTipIcon fontSize="small" /> },
    { path: "/terms", label: "Terms of Service", icon: <GavelIcon fontSize="small" /> },
    { path: "/contact", label: "Contact", icon: <ContactMailIcon fontSize="small" /> },
  ]

  return (
    <AppBar
      position="sticky"
      sx={{
        background: scrolled ? "rgba(0, 0, 0, 0.92)" : "rgba(0, 0, 0, 0.85)",
        borderBottom: "1px solid rgba(0, 229, 255, 0.1)",
        transition: "all 0.3s ease",
        boxShadow: scrolled ? "0 4px 20px rgba(0, 0, 0, 0.5)" : "none",
      }}
    >
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }

        @keyframes pulse-spotlight {
          0% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.4); }
          100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
        }

        @keyframes glow {
          0% { opacity: 0.8; }
          50% { opacity: 1; }
          100% { opacity: 0.8; }
        }
        
        .tab-active {
          position: relative;
        }

        .tab-active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background: #00e5ff;
          box-shadow: 0 0 8px #00e5ff, 0 0 12px #00e5ff;
          z-index: 1;
        }
        
        .avatar-glow {
          animation: avatar-pulse 2s infinite ease-in-out;
        }
        
        @keyframes avatar-pulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 229, 255, 0.4); }
          70% { box-shadow: 0 0 0 6px rgba(0, 229, 255, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 229, 255, 0); }
        }

        .nav-scroll-container {
          display: flex;
          overflow-x: auto;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
          scroll-behavior: smooth;
        }

        .nav-scroll-container::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
      `}</style>

      <Toolbar sx={{ justifyContent: "space-between", px: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden", flex: 1 }}>
          <RouterLink to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
            <Avatar
              src="/DarkEdge.ico"
              alt="DarkEdge"
              sx={{
                width: 40,
                height: 40,
                mr: { xs: 1, sm: 2 },
                border: "1px solid rgba(0, 229, 255, 0.3)",
                boxShadow: "0 0 10px rgba(0, 229, 255, 0.2)",
                flexShrink: 0,
              }}
            />
          </RouterLink>

          <Box sx={{ display: "flex", alignItems: "center", position: "relative", flex: 1, overflow: "hidden" }}>
            {/* Left scroll arrow - always show on small screens when needed */}
            {showLeftArrow && (
              <IconButton
                onClick={() => scrollNav("left")}
                size="small"
                sx={{
                  position: "absolute",
                  left: 0,
                  zIndex: 2,
                  bgcolor: "rgba(0, 0, 0, 0.8)",
                  color: theme.palette.primary.main,
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.9)" },
                  boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
                  width: 32,
                  height: 32,
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}

            {/* Scrollable navigation links */}
            <Box
              ref={navRef}
              className="nav-scroll-container"
              sx={{
                display: "flex",
                mx: showLeftArrow || showRightArrow ? 4 : 0,
                transition: "margin 0.3s ease",
                flex: 1,
                overflow: "hidden",
              }}
            >
              {navLinks.flatMap((link) =>
                link.external ? (
                  <Button
                    key={link.path}
                    onClick={() => handleExternalNavigation(link.path, link.name)}
                    startIcon={!isSmallScreen && link.icon}
                    sx={{
                      color: "white",
                      textTransform: "none",
                      px: { xs: 1.5, sm: 2 },
                      py: 1,
                      borderRadius: 0,
                      position: "relative",
                      overflow: "visible",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        "&::before": {
                          opacity: 0.8,
                        },
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderBottom: "3px solid transparent",
                        opacity: 0.5,
                        transition: "all 0.3s ease",
                      },
                    }}
                  >
                    {isSmallScreen ? link.icon : <Typography variant="body1">{link.label}</Typography>}
                  </Button>
                ) : (
                  [
                    <Button
                      key={link.path}
                      component={RouterLink}
                      to={link.path}
                      startIcon={!isSmallScreen && link.icon}
                      className={isActive(link.path) ? "tab-active" : ""}
                      sx={{
                        color: isActive(link.path) ? theme.palette.primary.main : "white",
                        textTransform: "none",
                        px: { xs: 1.5, sm: 2 },
                        py: 1,
                        borderRadius: 0,
                        position: "relative",
                        overflow: "visible",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                        flexShrink: 0,
                        textShadow: isActive(link.path) ? "0 0 10px rgba(0, 229, 255, 0.7)" : "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {isSmallScreen ? link.icon : <Typography variant="body1">{link.label}</Typography>}
                    </Button>,
                    link.path === "/" ? downloadDropdown : null,
                  ]
                ),
              )}

              {/* Other dropdown menu */}
              <Button
                id="other-menu-button"
                aria-controls={otherMenuOpen ? "other-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={otherMenuOpen ? "true" : undefined}
                onClick={handleOtherMenuOpen}
                endIcon={!isSmallScreen && <KeyboardArrowDownIcon />}
                className={isActive("/privacy") || isActive("/terms") || isActive("/contact") ? "tab-active" : ""}
                sx={{
                  color:
                    isActive("/privacy") || isActive("/terms") || isActive("/contact")
                      ? theme.palette.primary.main
                      : "white",
                  textTransform: "none",
                  px: { xs: 1.5, sm: 2 },
                  py: 1,
                  borderRadius: 0,
                  position: "relative",
                  overflow: "visible",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  flexShrink: 0,
                  textShadow:
                    isActive("/privacy") || isActive("/terms") || isActive("/contact")
                      ? "0 0 10px rgba(0, 229, 255, 0.7)"
                      : "none",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {isSmallScreen ? <MoreHorizIcon /> : <Typography variant="body1">Other</Typography>}
              </Button>
              <Menu
                id="other-menu"
                anchorEl={otherMenuAnchorEl}
                open={otherMenuOpen}
                onClose={handleOtherMenuClose}
                MenuListProps={{
                  "aria-labelledby": "other-menu-button",
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    background: "rgba(13, 15, 25, 0.95)",
                    border: "1px solid rgba(0, 229, 255, 0.2)",
                    boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
                  },
                }}
              >
                {otherLinks.map((link) => (
                  <MenuItem
                    key={link.path}
                    component={RouterLink}
                    to={link.path}
                    onClick={handleOtherMenuClose}
                    sx={{
                      color: isActive(link.path) ? theme.palette.primary.main : "white",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                      minWidth: 180,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      {link.icon}
                      <Typography variant="body2">{link.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Right scroll arrow - always show on small screens when needed */}
            {showRightArrow && (
              <IconButton
                onClick={() => scrollNav("right")}
                size="small"
                sx={{
                  position: "absolute",
                  right: 0,
                  zIndex: 2,
                  bgcolor: "rgba(0, 0, 0, 0.8)",
                  color: theme.palette.primary.main,
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.9)" },
                  boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
                  width: 32,
                  height: 32,
                }}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, flexShrink: 0 }}>
          {user ? (
            <>
              <Tooltip
                title={wsTooltip}
                arrow
                placement="bottom"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "rgba(13, 15, 25, 0.9)",
                      border: "1px solid rgba(0, 229, 255, 0.3)",
                      boxShadow: "0 0 15px rgba(0, 229, 255, 0.3)",
                      color: "white",
                      fontSize: "0.85rem",
                      maxWidth: "none",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      "& .MuiTooltip-arrow": {
                        color: "rgba(13, 15, 25, 0.9)",
                        "&::before": {
                          border: "1px solid rgba(0, 229, 255, 0.3)",
                          backgroundColor: "rgba(13, 15, 25, 0.9)",
                        },
                      },
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <FiberManualRecordIcon
                    sx={{
                      color: wsColor,
                      animation: wsAnimation ? wsAnimation : "none",
                      transition: "color 0.3s ease",
                      fontSize: "small",
                      position: "relative",
                      zIndex: 2,
                    }}
                    fontSize="small"
                  />

                  {/* Spotlight effect for connected status */}
                  {wsStatus === "connected" && (
                    <Box
                      sx={{
                        position: "absolute",
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(76, 175, 80, 0.6) 0%, rgba(0, 0, 0, 0) 70%)",
                        transform: "translate(-50%, -50%)",
                        top: "50%",
                        left: "50%",
                        zIndex: -1,
                        animation: "pulse-spotlight 2s infinite ease-in-out",
                      }}
                    />
                  )}
                </Box>
              </Tooltip>

              <Button
                component={RouterLink}
                to="/profile"
                className={isActive("/profile") ? "tab-active" : ""}
                sx={{
                  color: isActive("/profile") ? theme.palette.primary.main : "white",
                  textTransform: "none",
                  px: { xs: 1, sm: 2 },
                  borderRadius: 0,
                  position: "relative",
                  overflow: "visible",
                  textShadow: isActive("/profile") ? "0 0 10px rgba(0, 229, 255, 0.7)" : "none",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {isSmallScreen ? (
                  <Avatar
                    src={getDiscordAvatarUrl(user)}
                    alt={user.global_name || user.username}
                    className={isActive("/profile") ? "avatar-glow" : ""}
                    sx={{
                      width: 24,
                      height: 24,
                      border: isActive("/profile")
                        ? `1px solid ${theme.palette.primary.main}`
                        : "1px solid rgba(255, 255, 255, 0.3)",
                      boxShadow: isActive("/profile") ? "0 0 5px rgba(0, 229, 255, 0.5)" : "none",
                    }}
                  />
                ) : (
                  <>
                    <Avatar
                      src={getDiscordAvatarUrl(user)}
                      alt={user.global_name || user.username}
                      className={isActive("/profile") ? "avatar-glow" : ""}
                      sx={{
                        width: 24,
                        height: 24,
                        border: isActive("/profile")
                          ? `1px solid ${theme.palette.primary.main}`
                          : "1px solid rgba(255, 255, 255, 0.3)",
                        boxShadow: isActive("/profile") ? "0 0 5px rgba(0, 229, 255, 0.5)" : "none",
                        mr: 0.5,
                      }}
                    />
                    <Typography variant="body1" sx={{ display: { xs: "none", md: "block" } }}>
                      Profile
                    </Typography>
                  </>
                )}
              </Button>

              {isSmallScreen ? (
                <IconButton
                  color="error"
                  size="small"
                  onClick={handleLogoutClick}
                  sx={{
                    borderColor: alpha(theme.palette.error.main, 0.5),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleLogoutClick}
                  startIcon={<LogoutIcon />}
                  sx={{
                    ml: { xs: 0, sm: 1 },
                    borderColor: alpha(theme.palette.error.main, 0.5),
                    "&:hover": {
                      borderColor: theme.palette.error.main,
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  {!isMediumScreen && "Logout"}
                </Button>
              )}
            </>
          ) : (<LoginWithDiscordBtn isSmallScreen={isSmallScreen} />
          )}
        </Box>
      </Toolbar>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={confirmLogout}
      />
    </AppBar>
  )
}

export default Navbar
